import React from "react";

import { fractionToPercentage, inputStateForValue, useTranslation } from "utils";

import { INPUT_STATE, InputState, UNIT_DISPLAY_LABEL, getUnitDisplayLabel } from "consts";

import { AlertBanner, AlertBannerProps } from "components";

interface SanityCheckProps {
  inputValue: number;
  inputRange?: PracticeManagement.ParameterBounds;
  inputState?: InputState | null;
  showWhenWithinBounds?: boolean;
  unit?: "%" | keyof typeof UNIT_DISPLAY_LABEL | null;
}

const ICON_NAME_FOR_INPUT_STATE: Record<InputState, string> = {
  expected: "tabler/confetti",
  tooLowHard: "tabler/alert-triangle",
  tooLowSoft: "tabler/info-circle",
  tooHighSoft: "tabler/info-circle",
  tooHighHard: "tabler/alert-triangle",
};

const ALERT_BANNER_TYPE_FOR_INPUT_STATE: Record<InputState, AlertBannerProps["type"]> = {
  expected: "info-highlighted",
  tooLowHard: "error",
  tooLowSoft: "warning",
  tooHighSoft: "warning",
  tooHighHard: "error",
};

export const SanityCheck = ({ inputValue, inputRange, showWhenWithinBounds = false, unit }: SanityCheckProps) => {
  const { t } = useTranslation("misc");

  const inputState = inputStateForValue(inputValue, inputRange);

  if (inputState === INPUT_STATE.expected && !showWhenWithinBounds) return null;

  const [, displayMin, displayMax] = inputRange?.map((bound) => (unit === "%" ? fractionToPercentage(bound) : bound)) ?? [];

  return (
    <AlertBanner
      headingSize="h6"
      title={t(`sanityCheck.title.${inputState}`)}
      description={
        inputState === INPUT_STATE.expected
          ? t("sanityCheck.description.expected")
          : t(`sanityCheck.description.${inputState}`, { min: displayMin, max: `${displayMax} ${getUnitDisplayLabel(unit)}` })
      }
      type={ALERT_BANNER_TYPE_FOR_INPUT_STATE[inputState]}
      iconName={ICON_NAME_FOR_INPUT_STATE[inputState]}
      iconSize={24}
      iconAlign="start"
    />
  );
};
